import { Button, ButtonMode, ICONS, Loader } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement, useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { ACCEPT_FILES, MIME_TYPE } from './UploadFile.constants';

import styles from './UploadFile.module.scss';

const cx = classNames.bind(styles);

export type File = {
  name: string;
  fileIcon: ICONS;
  data: Uint8Array;
};

interface UploadFileProps {
  onSuccess: (file: File) => void;
  onError: () => void;
  isError: boolean;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
}

const getFileIcon = (fileType: string) => {
  switch (fileType) {
    case MIME_TYPE.XLSX:
    case MIME_TYPE.XLS:
      return ICONS.Xlsx;
    case MIME_TYPE.CSV:
      return ICONS.Csv;
    default:
      return ICONS.Xlsx;
  }
};

const UploadFile = ({ onSuccess, onError, isError, setIsLoading, isLoading }: UploadFileProps): ReactElement => {
  const onDrop = useCallback((acceptedFiles, fileRejections: FileRejection[]) => {
    setIsLoading(true);

    if (!isEmpty(fileRejections)) {
      onError();
      return;
    }

    const file = acceptedFiles[0];
    const reader = new FileReader();

    reader.onload = (event) => {
      const data = new Uint8Array(event.target?.result as ArrayBuffer);
      onSuccess({ name: file.name, fileIcon: getFileIcon(file.type), data });
    };

    reader.onerror = () => {
      onError();
    };

    reader.readAsArrayBuffer(file);
  }, []);

  const { getRootProps, getInputProps, open, isDragAccept } = useDropzone({
    onDrop,
    onError,
    noClick: true,
    accept: ACCEPT_FILES,
    maxFiles: 1,
  });

  const renderContent = () => (
    <div className={cx('content')}>
      <div className={cx('main')}>
        <p className={cx('title')} data-testid="title">
          Drag and drop or{' '}
          <Button mode={ButtonMode.PLAIN} onClick={open} className={cx('browse-button')}>
            browse your files
          </Button>
        </p>
        <p className={cx('sub-title')}>Only CSV or Excel files (.csv, .xls or .xlsx)</p>
      </div>
      {isError && <p className={cx('error')}>Error uploading file... please check your file type and try again</p>}
    </div>
  );

  return (
    <div {...getRootProps({ className: cx('body', { 'is-drag-accept': isDragAccept }) })}>
      <input data-testid="dropzone" {...getInputProps()} />
      {!isLoading && renderContent()}
      {isLoading && <Loader position="center" />}
    </div>
  );
};

export default UploadFile;
